<script setup lang="ts">
/**
 * The analytics consent banner (rl-lif9, contract A25, DPIA P6).
 *
 * Shown on every route, to visitors without an account and to signed-in
 * users alike, until they answer; `consent.analyticsChoiceOpen` decides
 * when. "Analytics settings" in either footer brings it back.
 *
 * **Equal weight is the floor** (Children's Code). The two answers are the
 * same button: one class, one grid cell each, so the same size, style and
 * prominence whatever the label length. Neither is pre-selected, and there
 * is no close control, so leaving the banner means answering it.
 *
 * **Not modal.** It sits at the end of the page flow, sticky to the bottom
 * of the viewport: it never traps focus, the page behind stays usable, and
 * scrolling to the end shows everything it covered. It takes focus only
 * when someone asks for it back from the footer.
 */
import { nextTick, ref, watch } from "vue";

import { useConsentStore } from "@/stores/consent";

defineProps<{
  /** In the app shell, clear the fixed desktop sidebar. */
  besideSidebar?: boolean;
}>();

const consent = useConsentStore();
const region = ref<HTMLElement | null>(null);

watch(
  () => consent.analyticsChoiceReopened,
  async (reopened) => {
    if (!reopened) return;
    await nextTick();
    region.value?.focus();
  },
);

// Only reachable when the server has superseded this build's wording; the
// fix is the build that carries the new wording, as in ConsentControls.
function reloadForNewPolicy(): void {
  window.location.reload();
}
</script>

<template>
  <section
    v-if="consent.analyticsChoiceOpen"
    ref="region"
    class="analytics-banner"
    :class="{ 'beside-sidebar': besideSidebar }"
    aria-labelledby="analytics-banner-title"
    aria-describedby="analytics-banner-body"
    tabindex="-1"
    data-testid="analytics-banner"
  >
    <div class="analytics-banner-card">
      <div class="analytics-banner-copy">
        <h2 id="analytics-banner-title" class="analytics-banner-title">
          Can we use analytics?
        </h2>
        <p id="analytics-banner-body" class="analytics-banner-body">
          We'd like to use analytics to see how the site is used, so we can
          improve it. It stays off unless you accept. Our public pages also
          count visits and loading speed without cookies; Reject turns that off
          too. You can change your mind at any time from "Analytics settings"
          at the bottom of the page.
          <RouterLink to="/privacy">Privacy notice</RouterLink>
        </p>
        <p
          v-if="consent.analyticsChoiceReopened"
          class="analytics-banner-status"
          data-testid="analytics-status"
        >
          Analytics is currently {{ consent.analyticsOn ? "on" : "off" }}.
        </p>
        <p v-if="consent.error" class="analytics-banner-alert" role="alert">
          {{ consent.error }}
          <button
            v-if="consent.policyOutdated"
            type="button"
            class="linklike"
            @click="reloadForNewPolicy"
          >
            Reload
          </button>
        </p>
      </div>
      <div class="analytics-banner-actions">
        <button
          type="button"
          class="secondary analytics-banner-answer"
          :disabled="consent.saving !== null"
          data-testid="analytics-accept"
          @click="consent.chooseAnalytics(true)"
        >
          Accept analytics
        </button>
        <button
          type="button"
          class="secondary analytics-banner-answer"
          :disabled="consent.saving !== null"
          data-testid="analytics-reject"
          @click="consent.chooseAnalytics(false)"
        >
          Reject
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Above the feedback button (200) and the sidebar (100), below modals. The
   strip around the card never swallows a click meant for the page. */
.analytics-banner {
  position: sticky;
  bottom: 0;
  z-index: 250;
  padding: 0 var(--sp-4) var(--sp-4);
  pointer-events: none;
}

.analytics-banner:focus {
  outline: none;
}

.analytics-banner-card {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-4) var(--sp-6);
  align-items: center;
  max-width: 960px;
  margin: 0 auto;
  padding: var(--sp-4) var(--sp-5);
  font-family: var(--rl-font-sans);
  background: var(--rl-surface-raised);
  border: 1px solid var(--rl-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  pointer-events: auto;
}

.analytics-banner:focus-visible .analytics-banner-card {
  box-shadow: var(--shadow-focus), var(--shadow-lg);
}

.analytics-banner-copy {
  flex: 1 1 22rem;
  min-width: 0;
}

.analytics-banner-title {
  margin: 0 0 var(--sp-1);
  font-size: var(--rl-text-lg);
  font-weight: 600;
  color: var(--rl-fg-1);
}

.analytics-banner-body,
.analytics-banner-status,
.analytics-banner-alert {
  margin: 0;
  font-size: var(--rl-text-md);
  line-height: 1.5;
  color: var(--rl-fg-2);
}

.analytics-banner-status {
  margin-top: var(--sp-2);
  color: var(--rl-fg-1);
}

.analytics-banner-alert {
  margin-top: var(--sp-2);
  color: var(--rl-danger-fg);
}

.analytics-banner-body a {
  color: var(--rl-fg-1);
}

/* Two equal columns: the answers stay the same size whatever their labels. */
.analytics-banner-actions {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
}

.analytics-banner-answer {
  min-width: 10rem;
  min-height: 44px;
}

@media (min-width: 768px) {
  .beside-sidebar {
    margin-left: var(--rl-sidebar-width);
  }
}

@media (max-width: 560px) {
  .analytics-banner {
    padding: 0 var(--sp-2) var(--sp-2);
  }

  .analytics-banner-card {
    padding: var(--sp-4);
  }

  .analytics-banner-actions {
    flex: 1 1 100%;
  }

  .analytics-banner-answer {
    min-width: 0;
  }
}
</style>
